import { createSelector, createFeatureSelector, select } from '@ngrx/store';
import { filter } from 'rxjs/operators';
import { pipe } from 'rxjs';
import { AUTH_FEATURE_NAME, PLAuthState } from './state';
import { User, UserGroupNames } from '../models';

const hasAnyGroups = (
  user: User | undefined,
  groups: UserGroupNames[],
  allowToSuperUser = false
) => {
  if (!user) {
    return false;
  }

  if (allowToSuperUser && !!user.is_superuser) {
    return true;
  }

  const { groups: userGroups = [] } = user;

  return groups.some((group) => userGroups.includes(group));
};

const hasAllGroups = (
  user: User | undefined,
  groups: UserGroupNames[],
  allowToSuperUser = false
) => {
  if (!user) {
    return false;
  }

  if (allowToSuperUser && !!user.is_superuser) {
    return true;
  }

  const { groups: userGroups = [] } = user;

  return groups.every((group) => userGroups.includes(group));
};

export const selectAuth = createFeatureSelector<PLAuthState>(AUTH_FEATURE_NAME);
export const selectCurrentToken = createSelector(
  selectAuth,
  (auth) => auth.token
);
export const selectCurrentUser = createSelector(
  selectAuth,
  (auth) => auth.currentUser
);
export const selectLoadedCurrentUser = pipe(
  select(selectCurrentUser),
  filter((user) => !!user)
);
export const selectCurrentUserLoaded = createSelector(
  selectCurrentUser,
  (user) => !!user && !!user.uuid
);
export const selectCurrentUserId = createSelector(
  selectCurrentUser,
  (user) => user?.uuid
);
export const selectAuthState = createSelector(
  selectAuth,
  (state) => state.authState
);

export const selectHasAnyGroups = (
  groups: UserGroupNames[],
  allowToSuperUser?: boolean
) =>
  createSelector(selectCurrentUser, (user) =>
    hasAnyGroups(user, groups, allowToSuperUser)
  );

export const selectHasAllGroups = (
  groups: UserGroupNames[],
  allowToSuperUser?: boolean
) =>
  createSelector(selectCurrentUser, (user) =>
    hasAllGroups(user, groups, allowToSuperUser)
  );

export const selectIsPaid = createSelector(
  selectHasAllGroups([
    UserGroupNames.LIMITED_ROOM_ACCESS,
    UserGroupNames.LIMITED_SAAS_ACCESS,
  ]),
  (isFreemium) => !isFreemium
);

export const selectIsTechSupport = selectHasAllGroups(
  [UserGroupNames.TECH_SUPPORT],
  true
);
